import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Typography, Box, Hidden, IconButton, Snackbar, Alert
    , FormControl, InputLabel, Select } from '@mui/material';
import { PersonAdd as IconPersonAdd, Search as IconSearch
    , Delete as IconDelete } from "@mui/icons-material";
import { Link } from "react-router-dom";
import * as evaluacionService from "../../services/evaluacionService";
import * as catalogoService from "../../services/catalogoService";
import DataGridBase from "../../components/common/DataGridBase";
import DialogYesNo from '../../components/DialogYesNo';
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Navegabilidad del catalogo de objetivos: los objetivos que se pueden
// asignar a los participantes, agrupados por tema de evaluacion.
const Index = () => {
    const { isReadOnly } = useMantenimiento("evaluacion");
    const [results, setResults] = useState([]);
    const [listTemas, setListTemas] = useState([]);
    const [listEstados, setListEstados] = useState([]);
    const [idTema, setIdTema] = useState(0);
    const [idEstado, setIdEstado] = useState(0);
    const [cargando, setCargando] = useState(true);
    const [openDialogDelete, setOpenDialogDelete] = useState(false);
    const [selectId, setSelectId] = useState(null);
    const [mensaje, setMensaje] = useState({ severity: "success", text: "" });
    const [openMessage, setOpenMessage] = useState(false);
    const navigate = useNavigate();

    const fetchTemas = useCallback(async () => {
        const data = await evaluacionService.getsTemaEvaluacionView()
            .catch((err) => {
                console.log("Err: ", err);
                return [];
            });

        // Los grupos (tema padre) no se listan: solo los temas evaluables.
        setListTemas((data || []).filter((row) => row.idTemaPadre != null));
    }, [])

    const fetchEstados = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(1)
            .catch((err) => {
                console.log("Err: ", err);
                return [];
            });
        setListEstados(data || []);
    }, [])

    const fetchResults = useCallback(async () => {
        setCargando(true);

        await evaluacionService.getsObjetivoCatalogoView(0)
            .then((data) => {
                let lista = data || [];

                if (idTema > 0) {
                    lista = lista.filter((row) => row.idTemaEvaluacion == idTema);
                }

                if (idEstado > 0) {
                    lista = lista.filter((row) => row.idEstado == idEstado);
                }

                setResults(lista);
                setCargando(false);
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });
    }, [idTema, idEstado])

    useEffect(() => {
        fetchTemas();
        fetchEstados();
    }, [fetchTemas, fetchEstados])

    useEffect(() => {
        fetchResults();
    }, [fetchResults])

    const deleteEntity = async () => {
        await evaluacionService.deleteObjetivoCatalogo(selectId)
            .then(() => {
                fetchResults();
            })
            .catch((err) => {
                // El objetivo ya asignado a alguien no se borra: se avisa.
                setMensaje({ severity: "error", text: err?.response?.data?.detail || "Hubo un error al borrar" });
                setOpenMessage(true);
            });
    }

    const columnas = useMemo(() => [
        { field: 'id', headerName: 'ID', width: 50, minWidth: 45, sortable: true, headerAlign: 'center', align: 'center',
            renderCell: (p) => <span style={{ fontSize: '0.75rem', color: '#666', fontFamily: 'monospace' }}>{p.value != null ? p.value : p.row.id}</span> },
        { field: 'nombre', headerName: 'Nombre', width: 260, sortable: false, headerAlign: 'center'
            , renderCell: (cellValues) => {
                return (
                    <Link to={'/objetivocatalogo/edit/' + cellValues.row.id}
                        style={{ color: 'inherit' }}>
                        {cellValues.row.nombre}
                    </Link>
                );
            }
        },
        { field: 'tema', headerName: 'Tema', width: 220, sortable: false, headerAlign: 'center' },
        { field: 'descripcion', headerName: 'Descripcion', width: 320, sortable: false, headerAlign: 'center' },
        { field: 'estado', headerName: 'Estado', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'delete', headerName: '', width: 80, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                return <IconButton aria-label="delete" size='small' style={{ height: 10 }} align={"center"}
                    disabled={isReadOnly} onClick={() => {
                        setSelectId(cellValues.row.id);
                        setOpenDialogDelete(true);
                    }}>
                    <IconDelete />
                </IconButton>;
            }
        },
    ], [isReadOnly])

    return (
        <div>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Catalogo de Objetivos
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Grid container item xs={12} sm={12} spacing={1}>
                        <Grid item xs={12} sm={5}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-tema" shrink>Tema</InputLabel>
                                <Select
                                    native
                                    label="Tema"
                                    value={idTema}
                                    onChange={(event) => {
                                        setIdTema(parseInt(event.target.value || "0"))
                                    }}
                                    inputProps={{
                                        name: 'idTema',
                                        id: 'input-tema',
                                    }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Todos)"}</option>
                                    {listTemas.map((row) => (
                                        <option key={row.id} value={row.id}>{row.temaPadre + " - " + row.nombre}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-estado" shrink>Estado</InputLabel>
                                <Select
                                    native
                                    label="Estado"
                                    value={idEstado}
                                    onChange={(event) => {
                                        setIdEstado(parseInt(event.target.value || "0"))
                                    }}
                                    inputProps={{
                                        name: 'idEstado',
                                        id: 'input-estado',
                                    }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Todos)"}</option>
                                    {listEstados.map((row) => (
                                        <option key={row.id} value={row.id}>{row.descripcion}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                            <Button variant="outlined" fullWidth size="medium" onClick={() => { fetchResults() }}>
                                {<Hidden smDown></Hidden>}
                                <IconSearch />
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        <DataGridBase
                            rows={results}
                            columns={columnas}
                            loading={cargando}
                            toolbarButtons={[
                                {
                                    text: "NUEVO",
                                    icon: <IconPersonAdd />,
                                    disabled: isReadOnly,
                                    onClick: () => navigate("/objetivocatalogo/edit/0")
                                }
                            ]}
                        />
                    </Box>
                </Grid>
                <DialogYesNo open={openDialogDelete} openClose={() => { setOpenDialogDelete(!openDialogDelete) }} setYes={deleteEntity}
                    setNo={() => { return }}
                    titulo={"Borrar Objetivo"}
                    texto={"Esta seguro de borrar el objetivo del catalogo?"} >
                </DialogYesNo>
                <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => { setOpenMessage(false) }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => { setOpenMessage(false) }} severity={mensaje.severity}>
                        {mensaje.text}
                    </Alert>
                </Snackbar>
            </Grid>
        </div>
    )
}

export default Index
